import React, { useState, useEffect, useCallback } from 'react';
import { messageAPI } from '../services/api.js';
import { api } from '../api/client.js';
import { copyWhatsAppMessage } from '../utils/whatsappMessage.js';
import './AdminMessages.css';

export const MESSAGE_CATEGORIES = {
    general: '🌱 General',
    motivation: '🔥 Motivación',
    focus: '🎯 Enfoque',
    productivity: '🚀 Productividad',
    constante: '🟢 Perfil: Constante',
    impulsivo: '🔴 Perfil: Impulsivo',
    analitico: '🔵 Perfil: Analítico',
    social: '🟡 Perfil: Social',
    estado_mal: '💙 Ánimo: día difícil',
    frase_pero: '✨ Percepción (Pero → Y)',
    frase_por_que: '🔄 Causalidad (¿Por qué? → ¿Para qué?)',
    frase_y: '💎 Refuerzo (Y)',
    fase_escuchar: '👂 EPA: Escucha',
    fase_perdonar: '🤲 EPA: Procesa',
    fase_adaptar: '🌊 EPA: Ajusta',
    recordatorio_checkin: '⏰ Recordatorio check-in',
    guia_operario: '🧭 Guía operario',
    guia_supervisor: '🤝 Guía supervisor',
    acompanamiento_alerta: '🟡 Acompañar alerta',
};

const MIN_PER_CATEGORY = 10;

export default function MessagesGallery({ autoEnsureBank = true }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ensuring, setEnsuring] = useState(false);
    const [editingMsg, setEditingMsg] = useState(null);
    const [newMsg, setNewMsg] = useState({ type: 'general', content: '' });
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [copyHint, setCopyHint] = useState(null);

    const loadMessages = useCallback(async () => {
        const res = await messageAPI.getAdminMessages();
        setMessages(res.data?.data || []);
    }, []);

    const ensureBank = useCallback(async (silent = false) => {
        setEnsuring(true);
        try {
            const result = await api.ensureMessageBank();
            if (!silent && result?.inserted > 0) {
                alert(`Se añadieron ${result.inserted} mensajes al banco maestro (mínimo ${MIN_PER_CATEGORY} por categoría).`);
            }
            await loadMessages();
            return result;
        } catch (err) {
            if (!silent) alert(err.message || 'No se pudo completar el banco de mensajes');
            throw err;
        } finally {
            setEnsuring(false);
        }
    }, [loadMessages]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                if (autoEnsureBank) {
                    try {
                        await api.ensureMessageBank();
                    } catch (e) {
                        console.warn('ensure-bank', e);
                    }
                }
                if (!cancelled) await loadMessages();
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [autoEnsureBank, loadMessages]);

    async function handleSave(e) {
        e.preventDefault();
        try {
            if (editingMsg) {
                await messageAPI.updateMessage(editingMsg.id, editingMsg);
            } else {
                await messageAPI.createMessage(newMsg);
                setNewMsg({ type: selectedCategory !== 'all' ? selectedCategory : 'general', content: '' });
            }
            setEditingMsg(null);
            await loadMessages();
        } catch (err) {
            alert('Error al guardar: ' + (err.message || 'desconocido'));
        }
    }

    async function handleDelete(id) {
        if (!window.confirm('¿Eliminar este mensaje del banco?')) return;
        try {
            await messageAPI.deleteMessage(id);
            await loadMessages();
        } catch (err) {
            alert('Error al eliminar');
        }
    }

    const countByType = (type) => messages.filter((m) => m.type === type).length;

    const categoriesBelowMin = Object.keys(MESSAGE_CATEGORIES).filter((t) => countByType(t) < MIN_PER_CATEGORY);

    const filteredMessages =
        selectedCategory === 'all' ? messages : messages.filter((m) => m.type === selectedCategory);

    const typesToShow =
        selectedCategory === 'all' ? Object.keys(MESSAGE_CATEGORIES) : [selectedCategory];

    if (loading) {
        return (
            <div className="flex items-center gap-sm text-muted py-lg">
                <div className="spinner" style={{ width: 28, height: 28 }} />
                Cargando galería de mensajes…
            </div>
        );
    }

    return (
        <div className="admin-bank-layout">
            <aside className="category-sidebar">
                <div className="glass-card p-sm mb-md" style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                    <strong>Mínimo {MIN_PER_CATEGORY} por categoría.</strong>
                    {categoriesBelowMin.length > 0 ? (
                        <p className="text-muted" style={{ margin: '0.35rem 0 0' }}>
                            Faltan mensajes en {categoriesBelowMin.length} categoría(s).
                        </p>
                    ) : (
                        <p className="text-muted" style={{ margin: '0.35rem 0 0' }}>
                            Banco completo.
                        </p>
                    )}
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm w-100 mt-sm"
                        disabled={ensuring}
                        onClick={() => void ensureBank(false)}
                    >
                        {ensuring ? 'Completando…' : 'Completar banco maestro'}
                    </button>
                </div>
                <nav className="category-nav">
                    <button
                        type="button"
                        className={`cat-nav-item ${selectedCategory === 'all' ? 'active' : ''}`}
                        onClick={() => setSelectedCategory('all')}
                    >
                        <span className="icon">🌍</span>
                        <span className="label">Todo ({messages.length})</span>
                    </button>
                    {Object.entries(MESSAGE_CATEGORIES).map(([val, label]) => {
                        const count = countByType(val);
                        const low = count < MIN_PER_CATEGORY;
                        return (
                            <button
                                key={val}
                                type="button"
                                className={`cat-nav-item ${selectedCategory === val ? 'active' : ''}${low ? ' cat-nav-item-warn' : ''}`}
                                onClick={() => setSelectedCategory(val)}
                            >
                                <span className="icon">{label.split(' ')[0]}</span>
                                <span className="label">
                                    {label.split(' ').slice(1).join(' ')} ({count}/{MIN_PER_CATEGORY})
                                </span>
                            </button>
                        );
                    })}
                </nav>
            </aside>

            <div className="admin-bank-content">
                <div className="glass-card p-md shadow-sm mb-lg">
                    <h3>{editingMsg ? '📝 Editando mensaje' : '✨ Nuevo mensaje'}</h3>
                    <p className="text-muted text-sm" style={{ marginTop: 0 }}>
                        Canal oficial: app ALINEA. También puedes <strong>copiar a WhatsApp</strong> el mismo texto
                        (sin envío automático aún).
                    </p>
                    {copyHint ? <p className="text-sm" style={{ color: 'var(--color-bien, #0f766e)' }}>{copyHint}</p> : null}
                    <form onSubmit={handleSave} className="horizontal-form-optimized">
                        <select
                            className="input-compact"
                            value={editingMsg ? editingMsg.type : newMsg.type}
                            onChange={(e) =>
                                editingMsg
                                    ? setEditingMsg({ ...editingMsg, type: e.target.value })
                                    : setNewMsg({ ...newMsg, type: e.target.value })
                            }
                        >
                            {Object.entries(MESSAGE_CATEGORIES).map(([val, label]) => (
                                <option key={val} value={val}>
                                    {label}
                                </option>
                            ))}
                        </select>
                        <textarea
                            className="input-compact"
                            rows="1"
                            style={{ resize: 'none' }}
                            value={editingMsg ? editingMsg.content : newMsg.content}
                            onChange={(e) =>
                                editingMsg
                                    ? setEditingMsg({ ...editingMsg, content: e.target.value })
                                    : setNewMsg({ ...newMsg, content: e.target.value })
                            }
                            required
                            placeholder="Un mensaje que acompañe el día en planta…"
                        />
                        <button type="submit" className="btn btn-primary">
                            {editingMsg ? 'Guardar' : 'Publicar'}
                        </button>
                        {editingMsg ? (
                            <button type="button" className="btn btn-secondary" onClick={() => setEditingMsg(null)}>
                                Cancelar
                            </button>
                        ) : null}
                    </form>
                </div>

                <div className="msg-groups-list">
                    {typesToShow.map((type) => {
                        const list = filteredMessages.filter((m) => m.type === type);
                        const label = MESSAGE_CATEGORIES[type];
                        const count = countByType(type);
                        if (selectedCategory !== 'all' && list.length === 0) {
                            return (
                                <div key={type} className="category-section mb-xl">
                                    <h4 className="category-header-clean mb-md">{label}</h4>
                                    <p className="text-muted text-sm">Sin mensajes en esta categoría. Usa «Completar banco maestro» o publica uno arriba.</p>
                                </div>
                            );
                        }
                        if (selectedCategory === 'all' && list.length === 0) return null;
                        return (
                            <div key={type} className="category-section mb-xl">
                                <h4 className="category-header-clean mb-md">
                                    {label}{' '}
                                    <span className={`cat-count-badge${count < MIN_PER_CATEGORY ? ' cat-count-low' : ''}`}>
                                        {count}/{MIN_PER_CATEGORY}
                                    </span>
                                </h4>
                                <div className="messages-grid-optimized">
                                    {list.map((m) => (
                                        <div key={m.id} className="glass-card msg-card-premium">
                                            <div className="card-top">
                                                <span className="id-tag">#{m.id}</span>
                                                <div className="card-actions">
                                                    <button
                                                        type="button"
                                                        className="btn-mini"
                                                        title="Copiar para WhatsApp"
                                                        onClick={async () => {
                                                            await copyWhatsAppMessage(m.content);
                                                            setCopyHint('Mensaje copiado para WhatsApp.');
                                                        }}
                                                    >
                                                        WA
                                                    </button>
                                                    <button type="button" className="btn-mini" onClick={() => setEditingMsg(m)}>
                                                        ✏️
                                                    </button>
                                                    <button type="button" className="btn-mini delete" onClick={() => handleDelete(m.id)}>
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>
                                            <p className="card-body">"{m.content}"</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
